import { Divider } from "$root/components/Divider";
import { IconWalls } from "$root/components/IconWall";
import { asOfMissingDataWall } from "$root/pages/PortfolioDetails/as-of/missingDataWall";
import { isAsOfReadable } from "$root/pages/PortfolioDetails/as-of/useAsOfSnapshot";
import { useLocaleFormatters } from "$root/localization/hooks";
import { currencyCode } from "$root/utils/currency";
import {
	type PortfolioWidgetData,
	PortfolioQueryWidgetBase,
	WidgetStatus,
} from "$root/pages/PortfolioDetails/PortfolioWidgetStatus";
import { useMappedQueries } from "$root/third-party-integrations/react-query/hooks";
import { commonGroupColumns } from "$root/ui-lib/common-table-columns";
import { type ContextContent, withContext } from "$root/utils";
import { PortfolioContext } from "$root/widgets-architecture/contexts/portfolio";
import { useWidgetOptions } from "$root/widgets-architecture/layout/WidgetsMapper/context";
import {
	type TableWithGroupsProps,
	AutoSortTableWithGroups,
	DefaultTabTitle,
	Tab,
	TabGroup,
	Text,
} from "@mdotm/shadcn/legacy/components";
import type { Currencies } from "@eu.mdotm.api/portfolio-lab";
import { themeCSSVars } from "@mdotm/shadcn/legacy/themes";
import { builtInSortFnFor } from "@mdotm/shadcn/legacy/utils";
import { useQuery } from "@tanstack/react-query";
import { useCallback, useMemo, useState } from "react";
import { portfolioRepository } from "$root/functional-areas/portfolio/repository";
import {
	toCouponGroups,
	toMaturityGroups,
	type CouponGroup,
	type CouponRow,
	type MaturityCouponTrackerData,
	type MaturityGroup,
	type MaturityRow,
} from "./data";

export type { CouponGroup, CouponRow, MaturityCouponTrackerData, MaturityGroup, MaturityRow } from "./data";

export type MaturityCouponTrackerInnerProps = MaturityCouponTrackerData & {
	/** Pinned as-of date, when the page has one: a section with no buckets then names it. */
	asOf?: string;
	/**
	 * Set for a portfolio in quantities (`null` = unset, shown as the default currency): each table gets
	 * an € column with group totals, where the data has any amount. `undefined` for a portfolio in
	 * weights, which shows no €.
	 */
	currency?: Currencies | null;
};

// Sequential color ramp used for both the bucket bar and the group band (short -> long duration).
const bucketColors = [
	themeCSSVars.palette_graph_R300,
	themeCSSVars.palette_S300,
	themeCSSVars.palette_graph_B300,
	themeCSSVars.palette_graph_V300,
];

/** Maps each bucket (by group name, in data order) to its color, shared by the bucket bar and the band column. */
function buildBucketColors(groups: Array<{ groupName: string }>): Map<string, string> {
	return new Map(groups.map((group, i) => [group.groupName, bucketColors[i % bucketColors.length]]));
}

/** Renders a titled row of adjacent colored segments, e.g. [0-1Y|5-10Y|10Y+], sized by bucket weight. */
function BucketBar({
	title,
	groups,
	colorByGroupName,
}: {
	title: string;
	groups: Array<{ groupName: string; shortName: string; weight: number }>;
	colorByGroupName: Map<string, string>;
}): React.JSX.Element | null {
	if (groups.length === 0) {
		return null;
	}
	return (
		<div className="mb-2 shrink-0">
			<Text as="div" type="Body/M/Bold" className="mb-1">
				{title}
			</Text>
			<div className="flex w-full overflow-hidden rounded">
				{groups.map((group) => (
					<div
						key={group.groupName}
						// Width is proportional to the bucket weight (flex-grow), while min-width auto — guaranteed by
						// whitespace-nowrap — plus padding keeps the short label readable even for tiny buckets.
						className="whitespace-nowrap px-2 py-1 text-center text-xs font-medium"
						style={{
							flexGrow: group.weight,
							flexBasis: 0,
							backgroundColor: colorByGroupName.get(group.groupName),
						}}
						title={`${group.groupName} (${Math.round(group.weight)}%)`}
					>
						{`${group.shortName} (${Math.round(group.weight)}%)`}
					</div>
				))}
			</div>
			<Divider className="my-2" />
		</div>
	);
}

export function MaturityCouponTrackerInner({
	maturityData,
	couponData,
	asOf,
	currency,
}: MaturityCouponTrackerInnerProps): React.JSX.Element {
	// Open on the section that actually has data: if the maturity section is empty while the coupon
	// section isn't, start on the coupon tab (and vice versa). Both-empty is handled upstream (the
	// whole widget shows the empty wall), so this only ever picks between one-empty configurations.
	const [currentTab, setCurrentTab] = useState(() => (maturityData.length === 0 && couponData.length > 0 ? 1 : 0));
	const { formatDate, formatNumber } = useLocaleFormatters();

	useWidgetOptions(
		() => ({
			title: "Maturity & Coupon tracker",
		}),
		[],
	);

	const showMoney = currency !== undefined;
	const code = currencyCode(currency);
	// The currency code is in the column header only. A missing amount reads N/A, while 0 is a value
	// and reads as such.
	const formatAmount = useCallback(
		(amount: number | null | undefined) => (amount == null ? "N/A" : formatNumber(amount, 2)),
		[formatNumber],
	);

	// Hide a date or € column entirely when nothing in the section carries one.
	const hasAnyExpirationDate = useMemo(
		() => maturityData.some((group) => group.rows.some((row) => row.expirationDate != null)),
		[maturityData],
	);
	const hasAnyCouponDate = useMemo(
		() => couponData.some((group) => group.rows.some((row) => row.couponDate != null)),
		[couponData],
	);
	const hasAnyMaturedValue = useMemo(
		() => showMoney && maturityData.some((group) => group.rows.some((row) => row.maturedValue != null)),
		[showMoney, maturityData],
	);
	const hasAnyCouponAmount = useMemo(
		() => showMoney && couponData.some((group) => group.rows.some((row) => row.couponAmount != null)),
		[showMoney, couponData],
	);

	const maturityColumns = useCallback<TableWithGroupsProps<MaturityGroup>["columns"]>(
		({ bandColumn, expandColumn }) => [
			bandColumn,
			expandColumn,
			{
				...commonGroupColumns.string("groupName", "tickerName"),
				header: "Instruments",
				// Buckets stay in their fixed (duration) order; only the rows inside each bucket sort.
				orderable: false,
				contentSortFn: builtInSortFnFor("tickerName"),
				content: (row) => row.instrumentName,
				groupContent: (group) => `${group.groupName} (${group.rows.length})`,
			},
			...(hasAnyExpirationDate
				? [
						{
							header: "Expiration date",
							align: "end" as const,
							cellClassName: "tabular-nums",
							groupContent: () => null,
							content: (row: MaturityRow) => (row.expirationDate ? formatDate(row.expirationDate) : "-"),
							contentSortFn: builtInSortFnFor("expirationDate"),
							name: "expirationDate",
						},
					]
				: []),
			...(hasAnyMaturedValue
				? [
						{
							// TODO(MDOT-25403): principal only, or principal plus the final coupon? To settle with the PM.
							header: `Matured value (${code})`,
							align: "end" as const,
							cellClassName: "tabular-nums",
							// TODO(MDOT-25403): whether the group total stays is open with the PM.
							groupContent: (group: MaturityGroup) => formatAmount(group.total),
							content: (row: MaturityRow) => formatAmount(row.maturedValue),
							contentSortFn: builtInSortFnFor<MaturityRow>("maturedValue"),
							name: "maturedValue",
						},
					]
				: []),
		],
		[formatDate, hasAnyExpirationDate, hasAnyMaturedValue, code, formatAmount],
	);

	const couponColumns = useCallback<TableWithGroupsProps<CouponGroup>["columns"]>(
		({ bandColumn, expandColumn }) => [
			bandColumn,
			expandColumn,
			{
				...commonGroupColumns.string("groupName", "tickerName"),
				header: "Instruments",
				// Buckets stay in their fixed (duration) order; only the rows inside each bucket sort.
				orderable: false,
				contentSortFn: builtInSortFnFor("tickerName"),
				content: (row) => row.instrumentName,
				groupContent: (group) => `${group.groupName} (${group.rows.length})`,
			},
			...(hasAnyCouponDate
				? [
						{
							header: "Coupon date",
							align: "end" as const,
							cellClassName: "tabular-nums",
							groupContent: () => null,
							content: (row: CouponRow) => (row.couponDate ? formatDate(row.couponDate) : "-"),
							contentSortFn: builtInSortFnFor("couponDate"),
							name: "couponDate",
						},
					]
				: []),
			{
				header: "Rate / Yield",
				align: "end",
				cellClassName: "tabular-nums",
				groupContent: () => null,
				content: (row) => (row.couponRate == null ? "N/A" : `${formatNumber(row.couponRate, 2)}%`),
				contentSortFn: builtInSortFnFor("couponRate"),
				name: "couponRate",
			},
			...(hasAnyCouponAmount
				? [
						{
							header: `Coupon amount (${code})`,
							align: "end" as const,
							cellClassName: "tabular-nums",
							// TODO(MDOT-25403): whether the group total stays is open with the PM, as on the maturity tab.
							groupContent: (group: CouponGroup) => formatAmount(group.total),
							content: (row: CouponRow) => formatAmount(row.couponAmount),
							contentSortFn: builtInSortFnFor<CouponRow>("couponAmount"),
							name: "couponAmount",
						},
					]
				: []),
		],
		[formatDate, formatNumber, hasAnyCouponDate, hasAnyCouponAmount, code, formatAmount],
	);

	const maturityColors = useMemo(() => buildBucketColors(maturityData), [maturityData]);
	const couponColors = useMemo(() => buildBucketColors(couponData), [couponData]);

	return (
		<div className="flex flex-col min-h-0 grow">
			{/* minHeightStrategy="0" lets the active tab's table shrink to the widget height and scroll internally. */}
			<TabGroup minHeightStrategy="0" palette="primary" tabIndex={currentTab} onTabChange={setCurrentTab}>
				<Tab
					className="flex flex-col min-h-0 grow"
					title={(titleProps) => <DefaultTabTitle {...titleProps}>Maturity date</DefaultTabTitle>}
				>
					{maturityData.length === 0 ? (
						(asOfMissingDataWall(asOf) ?? <IconWalls.NoBondsToTrack />)
					) : (
						<>
							<BucketBar
								title="Percentage distribution by maturity date"
								groups={maturityData}
								colorByGroupName={maturityColors}
							/>
							<AutoSortTableWithGroups
								groupedRows={maturityData}
								headerRowClassName="sticky top-0 z-10 bg-white"
								groupRowKey={(x) => x.groupName}
								groupColor={(group) => maturityColors.get(group.groupName) ?? ""}
								columns={maturityColumns}
							/>
						</>
					)}
				</Tab>
				<Tab
					className="flex flex-col min-h-0 grow"
					title={(titleProps) => <DefaultTabTitle {...titleProps}>Coupon date</DefaultTabTitle>}
				>
					{couponData.length === 0 ? (
						(asOfMissingDataWall(asOf) ?? <IconWalls.NoBondsToTrack />)
					) : (
						<>
							<BucketBar
								title="Percentage distribution by coupon date"
								groups={couponData}
								colorByGroupName={couponColors}
							/>
							<AutoSortTableWithGroups
								groupedRows={couponData}
								headerRowClassName="sticky top-0 z-10 bg-white"
								groupRowKey={(x) => x.groupName}
								groupColor={(group) => couponColors.get(group.groupName) ?? ""}
								columns={couponColumns}
							/>
						</>
					)}
				</Tab>
			</TabGroup>
		</div>
	);
}

const MaturityCouponTracker = ({ portfolio, asOf, asOfSnapshot }: ContextContent<typeof PortfolioContext>) => {
	useWidgetOptions(
		() => ({
			title: "Maturity & Coupon tracker",
		}),
		[],
	);

	// The dedicated tracker endpoint returns the buckets (with weights) plus their bonds/coupons in
	// one payload; split it into the maturity and coupon sections. Pinned to a past date it reads the
	// portfolio's snapshot like every other as-of widget, so it waits for that snapshot to be stored.
	const trackerQuery = useQuery({
		...portfolioRepository.maturityCouponTracker.getQueryConfig(portfolio?.uuid ?? "", { asOf }),
		enabled: isAsOfReadable(asOf, asOfSnapshot),
	});

	const query = useMappedQueries(
		([tracker]): PortfolioWidgetData<MaturityCouponTrackerData> => {
			const maturityData = toMaturityGroups(tracker);
			const couponData = toCouponGroups(tracker);
			// Empty buckets are dropped upstream, so no groups in either section means the portfolio
			// has no bonds/coupons to track at all — show the empty wall rather than two blank tabs.
			if (maturityData.length === 0 && couponData.length === 0) {
				return { widgetStatus: WidgetStatus.EMPTY, data: { maturityData, couponData } };
			}
			return {
				widgetStatus: WidgetStatus.READY,
				data: { maturityData, couponData },
			};
		},
		[],
		[trackerQuery],
	);

	return (
		<PortfolioQueryWidgetBase query={query} iconWalls={{ empty: <IconWalls.NoBondsToTrack /> }}>
			{({ maturityData, couponData }) => (
				<MaturityCouponTrackerInner
					maturityData={maturityData}
					couponData={couponData}
					asOf={asOf}
					currency={portfolio?.quantityMode ? (portfolio.baseCurrency ?? null) : undefined}
				/>
			)}
		</PortfolioQueryWidgetBase>
	);
};

export default withContext(PortfolioContext)(MaturityCouponTracker);
